import { collect } from 'collect.js';
import { FeatureFlags, PaymentGateway } from '../../services/exports/Constants';

/**
 * Pure onboarding-verification rules extracted from useVerification.jsx.
 *
 * These cover the field-completeness predicates (which depend on a `getValue`
 * accessor over the merchant's business profile) and the step-state derivation
 * (completion, progress, next active step). The hook stays responsible for
 * wiring Redux/context state and rendering each step's UI; all the decision
 * logic lives here so it can be unit-tested without React.
 *
 * See tests/hooks/verification/verificationRules.test.js.
 */

/**
 * Step required-ness must fail CLOSED: while the provider payload (or its
 * enabled_feature_flags) has not arrived, treat merchant billing as ON so the
 * Subscription/PaymentInfo steps stay required and onboarding cannot
 * auto-complete — and irreversibly stamp onboarding_complete_at — during a
 * payload gap (frontend deployed ahead of the backend, or
 * currentServiceProvider lagging currentCompany). Hiding money UI fails open
 * instead (see HasMerchantBillingGuard); the asymmetry is deliberate.
 */
export function resolveMerchantBillingForSteps(serviceProvider) {
  const flags = serviceProvider?.enabled_feature_flags;

  return Array.isArray(flags) ? flags.includes(FeatureFlags.MerchantBilling) : true;
}

const BASE_PERSONAL_FIELDS = [
  'owner_first_name',
  'owner_last_name',
  'owner_dob',
  'owner_address',
  'owner_city',
  'owner_state',
  'owner_country',
  'owner_zip_code',
];

/** Personal fields required for onboarding — US legal entities also need the owner id number. */
export function getRequiredPersonalFields(legalCountry) {
  return legalCountry === 'US' ? [...BASE_PERSONAL_FIELDS, 'owner_id_number'] : BASE_PERSONAL_FIELDS;
}

export function checkBusinessInfo(getValue) {
  return (
    Boolean(getValue('legal_name')) &&
    Boolean(getValue('legal_country')) &&
    Boolean(getValue('legal_state')) &&
    Boolean(getValue('legal_city')) &&
    Boolean(getValue('legal_address')) &&
    Boolean(getValue('legal_zip_code'))
  );
}

export function checkPersonalInfo(getValue, legalCountry) {
  return getRequiredPersonalFields(legalCountry).reduce((result, field) => result && Boolean(getValue(field)), true);
}

export function checkShippingInfo(getValue) {
  return (
    Boolean(getValue('shipping_company_name')) &&
    Boolean(getValue('shipping_salutation')) &&
    Boolean(getValue('shipping_first_name')) &&
    Boolean(getValue('shipping_last_name')) &&
    Boolean(getValue('shipping_address')) &&
    Boolean(getValue('shipping_zip_code')) &&
    Boolean(getValue('shipping_city')) &&
    Boolean(getValue('shipping_phone_number'))
  );
}

/**
 * Bank details are complete when the account number is present. Stripe additionally
 * requires the externally-stored stripe bank account id; Adyen does not.
 */
export function checkBankDetails(getValue, { paymentGateway, stripeBankAccountId }) {
  if (paymentGateway === PaymentGateway.Adyen) {
    return Boolean(getValue('bank_account_number'));
  }

  return Boolean(getValue('bank_account_number')) && Boolean(stripeBankAccountId);
}

export function checkTaxDetails(getValue) {
  return Boolean(getValue('tax_id'));
}

/** Delivery structure is captured once at least one delivery mode has been selected for the location. */
export function checkDeliveryStructure(deliveryModes) {
  return Array.isArray(deliveryModes) && deliveryModes.length > 0;
}

/** A step is done when finished, or skippable and explicitly skipped by the merchant. */
export function isStepFinished(step, skippedSteps) {
  return step.is_finished || (step?.is_skippable && collect(skippedSteps).contains(step.id));
}

/** Onboarding is complete when already stamped, or every required step is finished. */
export function isOnboardingComplete(steps, skippedSteps, onboardingCompleteAt) {
  return (
    !!onboardingCompleteAt ||
    collect(steps)
      .where('is_required', true)
      .every((item) => isStepFinished(item, skippedSteps))
  );
}

export function getRequiredSteps(steps) {
  return collect(steps).where('is_required').toArray();
}

/** First required, not-yet-finished step (the one the wizard should land on). */
export function findLastActiveStep(steps, skippedSteps) {
  return collect(steps).first((item) => item.is_required && !isStepFinished(item, skippedSteps));
}

export function getRemainingStepsCount(steps, skippedSteps) {
  return collect(steps)
    .filter((item) => item.is_required && !isStepFinished(item, skippedSteps))
    .count();
}

export function getProgress(steps, skippedSteps) {
  return {
    total: getRequiredSteps(steps).length,
    value: collect(steps).search((item) => item.id === findLastActiveStep(steps, skippedSteps)?.id),
  };
}
